/**
 * The one source of truth for "which domain do we offer to read?".
 *
 * Both intakes (the modal and the Brand empty state's inline picker) prefill
 * their URL field through `useBrandSources`, which reads the account's domains
 * and picks with `selectPrefillDomain`. The rules live here so the pick and the
 * validation intake applies before submitting stay one definition.
 */
import {
  isStarterDomain,
  isUsableCustomDomain,
  type SendingDisabledCandidate,
  type StarterDomainCandidate,
} from '@/services/db/domain/entity';

/**
 * A bare domain: no scheme, no path, no query, lowercased. Intake keeps a
 * source's path (see `normalizeSourceUrl`); this is for the domain alone.
 */
export function normalizeDomain(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '') // strip scheme
    .replace(/\/.*$/, '') // strip path
    .replace(/[#?].*$/, '') // strip query/hash
    .replace(/\.+$/, ''); // strip trailing dots
}

/**
 * Lenient domain check: one or more dot-separated labels ending in a 2+ letter
 * TLD. Accepts customer.io / lopeapp.com / sub.brand.co.uk; rejects bare words,
 * spaces, and obvious non-domains — without requiring a scheme.
 */
export function isLikelyDomain(value: string): boolean {
  return /^([\da-z](?:[\da-z-]*[\da-z])?\.)+[a-z]{2,}$/.test(value);
}

type DomainRecord = StarterDomainCandidate & SendingDisabledCandidate & { verified?: boolean | null };

/**
 * The account's own sending domain: a verified, sendable customer domain first,
 * then any customer-added domain, or null while the list is still loading (or
 * the account has none, or the one it has isn't usable).
 *
 * Starter domains are never offered. The backend provisions and auto-verifies
 * them, so `verified === true` alone would pick `warm-frost-1234.us.customerio.build`
 * for exactly the accounts that have no brand document yet, and the empty state
 * would offer to read a provisioned sending host instead of the brand's website.
 *
 * The `isLikelyDomain` filter is the same one intake applies before it will
 * submit a source. Without it a stored value that normalizes but wouldn't pass
 * validation still reaches the Brand empty state, which offers to "Generate
 * from" it and then drops the user into the intake form when the run can't
 * start. Rejecting it here means the offer is only ever made for a domain the
 * extraction will actually accept.
 */
export function selectPrefillDomain(domains: readonly DomainRecord[] | undefined): string | null {
  const customDomains = domains?.filter((d) => !isStarterDomain(d)) ?? [];
  const match = customDomains.find(isUsableCustomDomain) ?? customDomains[0];
  if (match == null) return null;
  const label = normalizeDomain(match.domain);
  return isLikelyDomain(label) ? label : null;
}
